import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import {
  IListBenefit,
  IBenefitCategoryContent,
  IBenefitShopItemsConfig,
  IEmployeeBenefit,
  IBenefit,
  IBenefitContent,
} from 'types/income';
import {
  exportXlsxConfig,
  getInvertedKeys,
  mergeOrgStructureIds,
} from 'utils/general';
import { toCents } from 'utils/general/to-cents';
import { isDefined } from 'utils/isDefined';
// keep this to avoid circular dependency
import { isAxiosError } from 'utils/query-client/query-client.errors';
import { Translations } from 'types/Common';
import { ShopItemsFilters } from 'store/shop';
import { FrequencyUnit, TableDataResponse } from 'types/general';
import {
  BenefitCategory,
  BenefitsFilters,
  EmployeeBenefitsFilters,
} from './benefits.types';

const getShopConfigFilters = (filters: Partial<ShopItemsFilters>) => ({
  filter: {
    name: filters.name || undefined,
    priceFrom: isDefined(filters.price?.from)
      ? toCents(filters.price.from)
      : undefined,
    priceTo: isDefined(filters.price?.to)
      ? toCents(filters.price.to)
      : undefined,
    itemIds: filters.items,
    countries: filters.countries,
    cities: filters.cities,
    supplierIds: filters.suppliers?.value,
    benefitPlanTypes: filters.benefitCategories,
    categories: filters.categories,
    invertedFilters: getInvertedKeys([
      { supplierIds: filters.suppliers?.isInverted },
    ]),
    ...(filters.taxExemptionCountries?.length &&
    filters.taxExemptionStatus?.length
      ? {
          taxExemptionCountries: filters.taxExemptionCountries,
          taxExemptionStatus: filters.taxExemptionStatus,
        }
      : {}),
  },
});

interface GetBenefit {
  benefitId: string;
}

interface GetBenefitContent {
  benefitId: string;
}

interface CreateBenefit {
  name: string;
  // TODO: rename to category
  type: BenefitCategory;
  templateId?: string;
}

interface UpdateBenefit {
  benefitId: string;
  data: {
    name: string;
    type: BenefitCategory;
    value: number | null;
    investmentAmount: number | null;
    allowClaimReimbursement: boolean;
    employeeContribution: number | null;
    investmentPeriod: FrequencyUnit;
    isDraft: boolean;
    isVisibleForEmployees: boolean;
    isBalanceTrackingEnabled: boolean;
    internalComment: string;
    previewDate: string | null;
    publishedAt: string | null;
    activatedAt: string | null;
    deactivatedAt: string | null;
    isArchived: boolean;
    allocationPriority: number;
    taxExemptionCountry: string | null;
  };
}

interface UpdateBenefitContent {
  benefitId: string;
  data: {
    translations: Translations<{
      title: string;
      description: string;
      imageId: string | null;
      links: Array<{ name: string; url: string; index: number }>;
      faqs: Array<{ question: string; answer: string; index: number }>;
      attachments: Array<{
        fileId: string | null;
        name: string;
        index: number;
      }>;
    }>;
  };
}

interface CopyBenefit {
  benefitId: string;
  name: string;
}

interface DeleteBenefit {
  benefitId: string;
}

interface GetBenefits {
  filters: Partial<BenefitsFilters>;
}

interface GetEmployeeBenefits {
  employeeId: string;
  filters: Partial<EmployeeBenefitsFilters>;
}

interface BulkUpdate {
  benefitIds: string[];
  data: Partial<Pick<UpdateBenefit['data'], 'isArchived'>>;
}

interface ExportXls {
  filters: Partial<BenefitsFilters>;
}

interface UpdateCategoryContent {
  category: BenefitCategory;
  data: {
    translations: Translations<{ label: string }>;
  };
}

interface AssignShopItem {
  benefitId: string;
  shopItemId: string;
}

interface UnassignShopItem {
  benefitId: string;
  shopItemId: string;
}

interface AssignShopItema {
  benefitId: string;
  filters: Partial<ShopItemsFilters>;
}

interface UnassignShopItema {
  benefitId: string;
  filters: Partial<ShopItemsFilters>;
}

interface GetShopItemsConfig {
  benefitId: string;
}

interface UpdateShopItemsConfig {
  benefitId: string;
  filters: Partial<ShopItemsFilters>;
}

interface RemoveShopItemsConfig {
  benefitId: string;
}

interface LinkSelection {
  benefitId: string;
}

export const loaders = {
  getBenefits: ({ filters }: GetBenefits) => {
    const orgStructure = mergeOrgStructureIds(filters);

    return api<TableDataResponse<IListBenefit>>({
      url: Endpoints.clientAdmin.benefits.root,
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        filter: {
          name: filters.name?.filter(Boolean),
          benefitIds: filters.benefits?.filter(Boolean),
          type: filters.category,
          status: filters.status,
          publishStatus: filters.publishStatus,
          deactivationDateFrom: filters.endsAt?.from,
          deactivationDateTo: filters.endsAt?.to,
          employeeCountFrom: filters.employeeCount?.from,
          employeeCountTo: filters.employeeCount?.to,
          investmentAmountFrom: filters.investmentAmount?.from,
          investmentAmountTo: filters.investmentAmount?.to,
          companyIds: orgStructure.companies,
          departmentIds: orgStructure.departments,
          teamIds: orgStructure.teams,
          isArchived: filters.showArchived === 'yes' ? undefined : false,
        },
        sort: filters.sort
          ? `${filters.sort.order === 'desc' ? '-' : ''}${
              filters.sort.columnKey
            }`
          : undefined,
      },
    });
  },
  getBenefit: ({ benefitId }: GetBenefit) => {
    return api<IBenefit>({
      url: Endpoints.clientAdmin.benefits.byId.root.replace(
        ':benefitId',
        benefitId,
      ),
    });
  },
  getBenefitContent: ({ benefitId }: GetBenefitContent) => {
    return api<IBenefitContent>({
      url: Endpoints.clientAdmin.benefits.byId.content.replace(
        ':benefitId',
        benefitId,
      ),
    });
  },
  createBenefit: (data: CreateBenefit) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.benefits.root,
      data,
      method: 'post',
    });
  },
  updateBenefit: ({ benefitId, data }: UpdateBenefit) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.benefits.byId.root.replace(
        ':benefitId',
        benefitId,
      ),
      data,
      method: 'put',
    });
  },
  updateBenefitContent: ({ benefitId, data }: UpdateBenefitContent) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.benefits.byId.content.replace(
        ':benefitId',
        benefitId,
      ),
      data,
      method: 'put',
    });
  },
  copyBenefit: ({ benefitId, name }: CopyBenefit) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.benefits.byId.copy.replace(
        ':benefitId',
        benefitId,
      ),
      data: { name },
      method: 'post',
    });
  },
  deleteBenefit: ({ benefitId }: DeleteBenefit) => {
    return api({
      url: Endpoints.clientAdmin.benefits.byId.root.replace(
        ':benefitId',
        benefitId,
      ),
      method: 'delete',
    });
  },
  bulkUpdate: ({ benefitIds, data }: BulkUpdate) => {
    return api({
      url: Endpoints.clientAdmin.benefits.root,
      data: {
        benefitIds,
        ...data,
      },
      method: 'patch',
    });
  },
  getEmployeeBenefits: ({ employeeId, filters }: GetEmployeeBenefits) => {
    return api<{ data: IEmployeeBenefit[] }>({
      url: Endpoints.clientAdmin.benefits.employee.replace(
        ':employeeId',
        employeeId,
      ),
      params: {
        filter: {
          trackBalance: filters.trackBalance || undefined,
        },
      },
    });
  },
  exportXls: ({ filters }: ExportXls) => {
    const orgStructure = mergeOrgStructureIds(filters);

    return api({
      url: Endpoints.clientAdmin.benefits.export.xls,
      params: {
        filter: {
          name: filters.name?.filter(Boolean),
          type: filters.category,
          status: filters.status,
          publishStatus: filters.publishStatus,
          deactivationDateFrom: filters.endsAt?.from,
          deactivationDateTo: filters.endsAt?.to,
          employeeCountFrom: filters.employeeCount?.from,
          employeeCountTo: filters.employeeCount?.to,
          investmentAmountFrom: filters.investmentAmount?.from,
          investmentAmountTo: filters.investmentAmount?.to,
          companyIds: orgStructure.companies,
          departmentIds: orgStructure.departments,
          teamIds: orgStructure.teams,
        },
        sort: filters.sort
          ? `${filters.sort.order === 'desc' ? '-' : ''}${
              filters.sort.columnKey
            }`
          : undefined,
      },
      ...exportXlsxConfig,
    });
  },
  getCategoriesContent: () => {
    return api<{ data: IBenefitCategoryContent[] }>({
      url: Endpoints.clientAdmin.benefits.categories.root,
    });
  },
  updateCategoryContent: ({ category, data }: UpdateCategoryContent) => {
    return api({
      url: Endpoints.clientAdmin.benefits.categories.byId.replace(
        ':category',
        category,
      ),
      data: {
        benefitCategoryOverrides: data.translations,
      },
      method: 'put',
    });
  },
  assignShopItem: ({ benefitId, shopItemId }: AssignShopItem) => {
    return api({
      url: Endpoints.clientAdmin.benefits.byId.byShopItem
        .replace(':benefitId', benefitId)
        .replace(':shopItemId', shopItemId),
      method: 'post',
    });
  },
  unassignShopItem: ({ benefitId, shopItemId }: UnassignShopItem) => {
    return api({
      url: Endpoints.clientAdmin.benefits.byId.byShopItem
        .replace(':benefitId', benefitId)
        .replace(':shopItemId', shopItemId),
      method: 'delete',
    });
  },
  assignShopItems: ({ benefitId, filters }: AssignShopItema) => {
    return api({
      url: Endpoints.clientAdmin.benefits.byId.bulk.assign.replace(
        ':benefitId',
        benefitId,
      ),
      params: getShopConfigFilters(filters),
      method: 'post',
    });
  },
  unassignShopItems: ({ benefitId, filters }: UnassignShopItema) => {
    return api<boolean>({
      url: Endpoints.clientAdmin.benefits.byId.bulk.unassign.replace(
        ':benefitId',
        benefitId,
      ),
      params: getShopConfigFilters(filters),
      method: 'post',
    });
  },
  getShopItemsConfig: ({ benefitId }: GetShopItemsConfig) => {
    return api<IBenefitShopItemsConfig>({
      url: Endpoints.clientAdmin.benefits.byId.shopItemsConfig.replace(
        ':benefitId',
        benefitId,
      ),
    }).catch((error: unknown) => {
      // 404 means no config exists yet - return null assignmentFilter
      // Other errors should propagate to trigger error handling
      if (isAxiosError(error) && error.response?.status === 404) {
        return { data: { assignmentFilter: null } };
      }
      throw error;
    });
  },
  updateShopItemsConfig: ({ benefitId, filters }: UpdateShopItemsConfig) => {
    return api({
      url: Endpoints.clientAdmin.benefits.byId.shopItemsConfig.replace(
        ':benefitId',
        benefitId,
      ),
      method: 'put',
      params: getShopConfigFilters(filters),
    });
  },
  removeShopItemsConfig: ({ benefitId }: RemoveShopItemsConfig) => {
    return api({
      url: Endpoints.clientAdmin.benefits.byId.shopItemsConfig.replace(
        ':benefitId',
        benefitId,
      ),
      method: 'delete',
    });
  },
  linkSelection: ({ benefitId }: LinkSelection) => {
    return api({
      url: Endpoints.clientAdmin.benefits.byId.bulk.linkSelection.replace(
        ':benefitId',
        benefitId,
      ),
      method: 'post',
    });
  },
};
